import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  color?: string;
}

interface PremiumDropdownProps {
  label?: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  icon: React.ReactNode;
  placeholder?: string;
  showSearch?: boolean;
  alignRight?: boolean;
  className?: string;
  variant?: 'default' | 'ghost' | 'list-item';
  disabled?: boolean;
  showLabel?: boolean;
}

const PremiumDropdown: React.FC<PremiumDropdownProps> = ({ 
  label, 
  value, 
  options, 
  onChange, 
  icon, 
  placeholder = 'Alle',
  showSearch = false,
  alignRight = false,
  className = '',
  variant = 'default',
  disabled = false,
  showLabel = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(search.toLowerCase()) || 
    opt.value === 'all' ||
    opt.value === ''
  );

  const getButtonStyles = () => {
    if (variant === 'list-item') {
      return `flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-300 w-full group ${
        isOpen ? 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900/30'
      }`;
    }
    
    if (variant === 'ghost') {
      return `flex items-center gap-2 px-2.5 rounded-lg transition-all duration-300 h-full w-full group ${
        value !== 'all' && value !== ''
          ? 'text-indigo-600 dark:text-indigo-400 font-bold' 
          : 'text-slate-500 dark:text-slate-400 hover:text-indigo-600 font-bold'
      }`;
    }
    
    return `flex items-center gap-2 px-2.5 rounded-lg transition-all duration-300 h-full w-full group ${
      value !== 'all' && value !== ''
        ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600 dark:text-indigo-400 font-bold' 
        : 'text-slate-400 dark:text-slate-500 hover:text-slate-500 hover:bg-white/50 dark:hover:bg-white/5 font-bold'
    }`;
  };

  if (disabled) {
    return (
      <div className={`flex items-center gap-2 px-2.5 rounded-lg opacity-50 cursor-not-allowed h-full text-slate-400 ${className}`}>
        {icon}
        <ChevronDown size={10} />
      </div>
    );
  }

  const hasHeightClass = className.includes('h-');
  return (
    <div className={`relative ${hasHeightClass ? '' : 'h-full'} ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={getButtonStyles()}
      >
        <div className={`transition-transform duration-300 ${isOpen ? 'scale-110' : 'group-hover:scale-110'} shrink-0`}>
          {(value !== 'all' && value !== '') && selectedOption?.icon ? (
            <div className="text-indigo-600 dark:text-indigo-400">{selectedOption.icon}</div>
          ) : (
            <div className="text-slate-400 group-hover:text-indigo-500 transition-colors">{icon}</div>
          )}
        </div>
        
        {(variant === 'list-item' || showLabel) && (
          <span className={`text-left font-bold truncate flex-1 ${variant === 'list-item' ? 'text-sm' : 'text-[11px] uppercase tracking-[0.1em]'}`}>
            {selectedOption?.label || placeholder}
          </span>
        )}

        <ChevronDown 
          size={10} 
          className={`transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180 text-indigo-500' : 'group-hover:text-indigo-500'}`} 
        />
      </button>

      {isOpen && (
        <div className={`absolute top-full mt-2 ${alignRight ? 'right-0' : 'left-0'} w-72 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-[100] overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top overflow-y-auto max-h-[400px] custom-scrollbar`}>
          {showSearch && (
            <div className="p-2 border-b border-slate-100 dark:border-slate-800 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  autoFocus
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Suche..."
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border-none rounded-lg pl-8 pr-3 py-1.5 text-[11px] focus:ring-1 ring-indigo-500/30 outline-none text-slate-700 dark:text-slate-200"
                />
              </div>
            </div>
          )}
          
          <div className="p-1.5">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest text-balance">
                Keine Ergebnisse
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left transition-all duration-200 group/item mb-0.5 ${
                    value === option.value 
                      ? 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800' 
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-lg transition-colors ${
                      value === option.value 
                        ? 'bg-white dark:bg-indigo-900/60 shadow-sm' 
                        : 'bg-slate-100 dark:bg-slate-800 group-hover/item:bg-white dark:group-hover/item:bg-slate-700'
                    }`}>
                      {option.icon ? (
                        <div className={value === option.value ? 'text-indigo-500' : 'text-slate-400 group-hover/item:text-indigo-400'}>
                          {option.icon}
                        </div>
                      ) : option.color ? (
                        <div className={`w-3 h-3 rounded-full ${option.color} shadow-sm`} />
                      ) : (
                        <div className="w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600" />
                      )}
                    </div>
                    <span className="text-[11px] font-bold tracking-tight uppercase">
                      {option.label}
                    </span>
                  </div>
                  {value === option.value && (
                    <div className="w-5 h-5 flex items-center justify-center bg-indigo-500 rounded-full text-white shadow-lg shadow-indigo-500/20">
                      <Check size={10} strokeWidth={3} />
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumDropdown;
